/**
 * LivePreview (Firma) — JASNY podgląd strony firmy.
 *
 * Spójny 1:1 z podglądem Hodowli / Suki / Reproduktora / Miotu: ten sam jasny szablon
 * (nagłówek „Twoja strona · podgląd na żywo" + LIVE + toggle desktop/mobile + pasek ekosystemu
 * + jasny canvas z białą kartą strony). Różni się WYŁĄCZNIE treścią — firma pokazuje
 * Ofertę / Realizacje / Opinie zamiast psów i miotów.
 */
import { useState } from 'react';
import {
  Monitor, Smartphone, Award, Shield, Clock, MapPin,
  Phone, Globe, Sparkles, Search, Check, Star, Briefcase,
} from 'lucide-react';
import { getImageUrl } from '@/utils/imageUrl';
import { industryName } from './industries';

const FEATURES = [
  { icon: Award, title: 'Doświadczenie', desc: 'Sprawdzona jakość i profesjonalizm.' },
  { icon: Shield, title: 'Pełne zaufanie', desc: 'Transparentne zasady i rzetelność.' },
  { icon: Clock, title: 'Szybki kontakt', desc: 'Sprawnie odpowiadamy na zapytania.' },
  { icon: MapPin, title: 'Lokalnie', desc: 'Działamy w Twojej okolicy.' },
];

const VISIBLE_IN = ['Google', 'Bing', 'Mapy', 'Katalog APISAI', 'Facebook', 'Asystenci'];

function priceLabel(s) {
  if (!s) return '';
  const v = s.price || '';
  switch (s.price_type) {
    case 'from': return v ? `od ${v} zł` : 'od ...';
    case 'hourly': return v ? `${v} zł/h` : '... zł/h';
    case 'free': return 'bezpłatne';
    case 'contact': return 'wycena indyw.';
    default: return v ? `${v} zł` : '';
  }
}

function HeroImg({ src }) {
  if (src) {
    return <img src={getImageUrl(src) || src} alt="" className="absolute inset-0 w-full h-full object-cover" />;
  }
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-orange-50 to-orange-50 flex items-center justify-center">
      <Briefcase className="w-12 h-12 text-orange-300" />
    </div>
  );
}

function LandingMock({ data, isMobile }) {
  const name = data.name?.trim() || 'Twoja firma';
  const city = data.city?.trim();
  const industryLabel = industryName(data.industry) || 'Firma usługowa';
  const description = data.description?.trim();
  const hook = data.short_description?.trim() || 'Profesjonalnie. Z pasją. Lokalnie.';
  const services = data.services || [];
  const portfolio = data.portfolio || [];
  const reviews = data.reviews || [];

  const containerCls = isMobile
    ? 'w-[300px] mx-auto border-[10px] border-gray-800 rounded-[32px] overflow-hidden shadow-2xl bg-white'
    : 'w-full rounded-xl overflow-hidden shadow-xl bg-white border border-gray-200';
  const titleSize = isMobile ? 'text-xl' : 'text-2xl';
  const heroPad = isMobile ? 'p-4' : 'p-8';

  return (
    <div className={containerCls} data-testid="live-preview-landing">
      {/* Top nav */}
      <div className={`flex items-center justify-between bg-white border-b border-gray-100 ${isMobile ? 'px-3 py-2' : 'px-5 py-3'}`}>
        <div className="flex items-center gap-2 min-w-0">
          {data.logo ? (
            <img src={getImageUrl(data.logo) || data.logo} alt="logo" className="w-6 h-6 rounded object-cover flex-shrink-0" />
          ) : (
            <div className="w-6 h-6 rounded bg-gradient-to-br from-orange-500 to-orange-600 flex-shrink-0" />
          )}
          <span className={`font-bold text-gray-900 truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>{name}</span>
        </div>
        {!isMobile && (
          <div className="flex items-center gap-3 text-[11px] text-gray-600">
            <span>Oferta</span>
            <span>Realizacje</span>
            <span>Opinie</span>
            <span>O nas</span>
            <span className="bg-orange-500 text-white px-2.5 py-1 rounded-md font-medium">Zapytaj</span>
          </div>
        )}
      </div>

      {/* Hero */}
      <div className={`relative ${isMobile ? 'h-44' : 'h-56'} w-full bg-gray-100`}>
        <HeroImg src={data.cover_photo} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
        <div className={`relative h-full flex flex-col justify-center ${heroPad} text-white`}>
          <span className={`inline-flex w-fit items-center gap-1 bg-white/90 text-gray-900 rounded-full font-semibold ${isMobile ? 'text-[9px] px-2 py-0.5' : 'text-[10px] px-2.5 py-1'} mb-2`}>
            <Briefcase className="w-3 h-3 text-orange-500" /> {industryLabel}
          </span>
          <h2 className={`font-bold leading-tight ${titleSize} max-w-xs`}>{hook}</h2>
          {city && (
            <p className={`mt-1 opacity-95 ${isMobile ? 'text-xs' : 'text-sm'} max-w-xs`}>{industryLabel} · {city}</p>
          )}
          <div className="flex gap-2 mt-3">
            <span className={`bg-orange-500 text-white rounded-md font-medium ${isMobile ? 'text-[10px] px-2 py-1' : 'text-xs px-3 py-1.5'}`}>Zobacz ofertę</span>
            <span className={`bg-white/95 text-gray-900 rounded-md font-medium ${isMobile ? 'text-[10px] px-2 py-1' : 'text-xs px-3 py-1.5'}`}>Skontaktuj się</span>
          </div>
        </div>
      </div>

      {/* O firmie */}
      <div className={isMobile ? 'p-3' : 'p-5'}>
        <h3 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'} mb-2`}>O naszej firmie</h3>
        {description ? (
          <p className={`text-gray-700 leading-relaxed ${isMobile ? 'text-[11px]' : 'text-xs'} line-clamp-4`}>{description}</p>
        ) : (
          <p className={`text-gray-400 italic ${isMobile ? 'text-[11px]' : 'text-xs'}`}>Tu pojawi się opis Twojej firmy…</p>
        )}

        {/* 4 features */}
        <div className={`grid grid-cols-2 gap-2 mt-3 ${isMobile ? '' : 'gap-3'}`}>
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex gap-2 items-start">
              <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-orange-500" />
              </div>
              <div className="min-w-0">
                <p className={`font-semibold text-gray-900 leading-tight ${isMobile ? 'text-[10px]' : 'text-[11px]'}`}>{title}</p>
                {!isMobile && <p className="text-[10px] text-gray-500 line-clamp-1">{desc}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Oferta / Usługi */}
      {services.length > 0 && (
        <div className={`bg-gray-50 ${isMobile ? 'p-3' : 'p-5'} border-t border-gray-100`}>
          <h3 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'} mb-3`}>Nasza oferta</h3>
          <div className="space-y-2">
            {services.slice(0, 3).map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
                <p className={`font-semibold text-gray-900 truncate ${isMobile ? 'text-[11px]' : 'text-xs'}`}>{s.name || 'Usługa'}</p>
                {priceLabel(s) && (
                  <span className={`text-orange-600 font-bold flex-shrink-0 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>{priceLabel(s)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Realizacje (portfolio) */}
      {portfolio.length > 0 && (
        <div className={`${isMobile ? 'px-3 py-2' : 'px-5 py-3'}`}>
          <p className={`font-semibold text-gray-700 ${isMobile ? 'text-[11px]' : 'text-xs'} mb-1.5`}>Realizacje</p>
          <div className="grid grid-cols-4 gap-1">
            {portfolio.slice(0, 4).map((p, i) => (
              <div key={i} className="aspect-square rounded bg-gray-100 overflow-hidden">
                <img src={getImageUrl(p.image || p) || (p.image || p)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Opinie */}
      {reviews.length > 0 && (
        <div className={`bg-gray-50 ${isMobile ? 'p-3' : 'p-5'} border-t border-gray-100`}>
          <h3 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'} mb-2`}>Opinie klientów</h3>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center gap-1 mb-1">
              {[0, 1, 2, 3, 4].map((idx) => (
                <Star key={idx} className={`w-3 h-3 ${idx < (Number(reviews[0].rating) || 5) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
              ))}
              <span className={`ml-1 font-semibold text-gray-900 ${isMobile ? 'text-[10px]' : 'text-[11px]'}`}>{reviews[0].author || 'Klient'}</span>
            </div>
            <p className={`text-gray-600 italic line-clamp-3 ${isMobile ? 'text-[10px]' : 'text-[11px]'}`}>"{reviews[0].text}"</p>
          </div>
        </div>
      )}

      {/* Contact CTA */}
      <div className={`bg-orange-50 border-t border-orange-100 ${isMobile ? 'p-3' : 'p-4'}`}>
        <p className={`font-bold text-gray-900 ${isMobile ? 'text-xs' : 'text-sm'}`}>Masz pytania? Napisz do nas.</p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {data.social_facebook && (
            <span className="flex items-center gap-1 px-2 py-1 bg-white rounded text-[10px] text-gray-700 border border-gray-200">
              <Globe className="w-2.5 h-2.5 text-blue-600" /> Facebook
            </span>
          )}
          {data.phone && (
            <span className="flex items-center gap-1 px-2 py-1 bg-white rounded text-[10px] text-gray-700 border border-gray-200">
              <Phone className="w-2.5 h-2.5 text-orange-500" /> {data.phone}
            </span>
          )}
          {!data.social_facebook && !data.phone && (
            <span className="text-[10px] text-gray-400 italic">Dodaj kontakt w kroku 6…</span>
          )}
        </div>
      </div>

      {/* Visible in */}
      <div className={`bg-gray-50 border-t border-gray-100 ${isMobile ? 'p-2.5' : 'p-3'}`}>
        <p className={`text-gray-500 ${isMobile ? 'text-[9px]' : 'text-[10px]'} text-center mb-1`}>Po opublikowaniu profil będzie widoczny w:</p>
        <p className={`text-orange-600 ${isMobile ? 'text-[9px]' : 'text-[10px]'} text-center font-medium`}>{VISIBLE_IN.join(' · ')}</p>
        {city && <p className={`text-gray-400 ${isMobile ? 'text-[9px]' : 'text-[10px]'} text-center mt-1`}>{city}</p>}
      </div>
    </div>
  );
}

export default function LivePreview({ data }) {
  const [mode, setMode] = useState('desktop');

  return (
    <div className="w-full" data-testid="business-wizard-v2-live-preview">
      {/* Header z toggle + ecosystem context — identyczny jak w pozostałych kreatorach */}
      <div className="mb-3 px-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-xs uppercase tracking-wider font-semibold text-gray-500">
              Twoja strona · podgląd na żywo
            </p>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold ring-1 ring-emerald-200/60">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              LIVE
            </span>
          </div>
          <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setMode('desktop')}
              data-testid="business-live-preview-mode-desktop"
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${mode === 'desktop' ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'}`}
              aria-label="Desktop"
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setMode('mobile')}
              data-testid="business-live-preview-mode-mobile"
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${mode === 'mobile' ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'}`}
              aria-label="Mobile"
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Ecosystem signals strip */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Globe className="w-3 h-3" /> Widoczne publicznie
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium ring-1 ring-blue-200/50">
            <Search className="w-3 h-3" /> Gotowe pod Google
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Sparkles className="w-3 h-3" /> Indeksowane w wyszukiwarkach
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-medium ring-1 ring-emerald-200/50">
            <Check className="w-3 h-3" /> Mobile-ready
          </span>
        </div>
      </div>

      {/* Canvas */}
      <div className="bg-gray-50 rounded-2xl p-4 min-h-[640px] flex items-start justify-center border border-gray-100">
        <LandingMock data={data} isMobile={mode === 'mobile'} />
      </div>
    </div>
  );
}
